import {
  Text,
  PercentageInput,
  InputSelectControlField,
  Tooltip,
  useModalState,
  Modal,
  Button,
  GreyGrey10,
  GreyGrey60,
  IconCircleInfo,
  Primary14SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { cloneElement, ReactElement, useState } from 'react'
import {
  decimalFromPercentage,
  percentageFromDecimal,
  useForm
} from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  countriesAlpha2,
  countriesAlpha2Emojis,
  type CountriesAlpha2,
  State,
  getStateOptions
} from '@sequencehq/api/commonEnums'
import isEmpty from 'lodash/isEmpty'
import { TaxCategory, TaxRate } from '../taxRates.types'
import { SUPPORTED_COUNTRIES_WITH_STATES } from '../taxRates.constants'

const NATIONWIDE = 'NATIONWIDE'

const NATIONWIDE_OPTION = {
  label: 'Nationwide',
  value: NATIONWIDE
}

const getAllStateOptions = (country: CountriesAlpha2) => [
  NATIONWIDE_OPTION,
  ...getStateOptions(country)
]

type FormValues = {
  country: CountriesAlpha2 | undefined
  taxCategories: Record<TaxCategory['id'], TaxRate['amount']>
  state?: string
}

type MiniTaxCategory = Pick<TaxCategory, 'id' | 'name'> & {
  description?: string
}

interface Props {
  trigger?: ReactElement | ((openModal: () => void) => ReactElement)
  onSubmit: (
    values: FormValues,
    taxRatesForCountry?: TaxRate[],
    state?: State
  ) => Promise<void>
  countriesWithTaxRates?: Record<CountriesAlpha2, TaxRate[]>
  preselectedCountry?: CountriesAlpha2
  preselectedState?: State
  options?: {
    closeOnCancel: boolean
    closeOnSubmit: boolean
    submitText?: string
    submittingText?: string
    onCancel?: () => void
    onClose?: () => void
  }
  taxCategories?: MiniTaxCategory[]
  filterTaxCategories?: (taxCategories: MiniTaxCategory[]) => MiniTaxCategory[]
  'data-testid'?: string
}

const getInitialValues = ({
  taxCategories,
  taxRatesForCountry,
  preselectedCountry,
  preselectedState,
  stateOptions
}: {
  taxCategories: Pick<TaxCategory, 'id' | 'name'>[]
  taxRatesForCountry?: TaxRate[]
  preselectedCountry?: CountriesAlpha2
  preselectedState?: State
  stateOptions?: { disabled: boolean; value: string }[]
}): FormValues => {
  const country = taxRatesForCountry?.[0]?.country

  // If a country is given, it means we are editing tax rates
  if (country) {
    const state = preselectedState ?? NATIONWIDE

    return {
      country,
      state: state,
      taxCategories: taxCategories.reduce(
        (acc, category) => {
          const taxRate = taxRatesForCountry?.find(
            rate => rate.taxCategoryId === category.id
          )

          return {
            ...acc,
            [category.id]: taxRate?.amount
              ? percentageFromDecimal(taxRate.amount)
              : ''
          }
        },
        {} as FormValues['taxCategories']
      )
    }
  }

  // Either passed preselected state or the first non-disabled state option
  const initialStateOption =
    preselectedState ||
    stateOptions?.filter(option => !option.disabled)?.[0]?.value

  return {
    country: preselectedCountry,
    state: initialStateOption,
    taxCategories: taxCategories.reduce(
      (acc, category) => {
        return {
          ...acc,
          [category.id]: ''
        }
      },
      {} as FormValues['taxCategories']
    )
  }
}

const convertAmountSafely = (amount: string | undefined) => {
  let convertedAmount = amount || ''
  if (amount) {
    try {
      convertedAmount = decimalFromPercentage(amount)
    } catch (e) {
      // Do nothing, keep the value as is
    }
  }

  return convertedAmount
}

const getModifiedTaxCategories = (
  formTaxCategories: Record<string, string>,
  initialValues: FormValues['taxCategories']
) => {
  return Object.entries(formTaxCategories).reduce(
    (acc, [taxCategoryId, amount]) => {
      const initialRate = initialValues[taxCategoryId]

      if (amount !== initialRate) {
        return {
          ...acc,
          [taxCategoryId]: convertAmountSafely(amount)
        }
      }

      return acc
    },
    {} as FormValues['taxCategories']
  )
}

const getTaxRatesForEditing = ({
  countriesWithTaxRates = {} as Record<CountriesAlpha2, TaxRate[]>,
  preselectedCountry,
  preselectedState
}: Pick<
  Props,
  'countriesWithTaxRates' | 'preselectedCountry' | 'preselectedState'
>) => {
  if (!preselectedCountry) {
    return []
  }

  const taxRatesForPreselectedCountry =
    countriesWithTaxRates[preselectedCountry] ?? []

  if (preselectedState) {
    // Return all tax rates for country that _are_ region specific
    return taxRatesForPreselectedCountry.filter(
      rate => rate.region === preselectedState
    )
  }

  // Return all tax rates for country that are not region specific
  return taxRatesForPreselectedCountry.filter(rate => !rate.region)
}

export const TaxRatesModalUI = ({
  onSubmit,
  countriesWithTaxRates = {} as Record<CountriesAlpha2, TaxRate[]>,
  preselectedCountry,
  preselectedState,
  options = {
    closeOnCancel: true,
    closeOnSubmit: true
  },
  taxCategories: externalTaxCategories,
  filterTaxCategories,
  onClose,
  'data-testid': dataTestId
}: Omit<Props, 'trigger'> & { onClose: () => void }) => {
  const taxRatesForCountry = getTaxRatesForEditing({
    countriesWithTaxRates,
    preselectedCountry,
    preselectedState
  })
  const isEditing = taxRatesForCountry.length > 0
  const submitText =
    options.submitText ?? (isEditing ? 'Save changes' : 'Add tax rate')
  const submittingText =
    options.submittingText ?? (isEditing ? 'Saving...' : 'Adding...')

  const { data } = useQuery(
    dashboardv99990101Client.getTaxCategories,
    {},
    {
      enabled: !externalTaxCategories
    }
  )

  const loadedTaxCategories = data?.items ?? externalTaxCategories ?? []
  const taxCategories = filterTaxCategories
    ? filterTaxCategories(loadedTaxCategories)
    : loadedTaxCategories

  const taxRateCategoriesFields: {
    property: `taxCategories.${string}`
  }[] = taxCategories.map(category => ({
    property: `taxCategories.${category.id}`
  }))

  const alreadyDefinedStateTaxRates = SUPPORTED_COUNTRIES_WITH_STATES.flatMap(
    stateSupportedCountry => {
      return (
        countriesWithTaxRates[stateSupportedCountry]?.map(({ region }) =>
          region === undefined ? NATIONWIDE : region
        ) ?? []
      )
    }
  )

  // Initially get options for preselected country (editing) or the first supported country
  const initialStateOptions = getAllStateOptions(
    preselectedCountry || SUPPORTED_COUNTRIES_WITH_STATES[0]
  ).map(option => {
    return {
      ...option,
      disabled: alreadyDefinedStateTaxRates.includes(option.value)
    }
  })

  const initialValues = getInitialValues({
    taxCategories,
    taxRatesForCountry,
    preselectedCountry,
    stateOptions: initialStateOptions,
    preselectedState
  })

  const [formData, setFormData] = useState<FormValues>(initialValues)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { fields: fieldsConfig, queries } = useForm<FormValues>({
    value: initialValues,
    showValidationErrors: false,
    fieldConfiguration: [
      {
        property: 'country',
        validation: [required],
        disabled: () => isEditing || Boolean(preselectedCountry)
      },
      {
        property: 'state',
        validation: [],
        disabled: () => isEditing
      },
      ...taxRateCategoriesFields
    ],
    onChange: newData => {
      setFormData(newData)
    }
  })

  // Dynamically get state options based on currently selected country
  const stateOptions = getAllStateOptions(
    queries.formData.country || SUPPORTED_COUNTRIES_WITH_STATES[0]
  ).map(option => {
    return {
      ...option,
      disabled: alreadyDefinedStateTaxRates.includes(option.value)
    }
  })

  const modifiedTaxCategories = getModifiedTaxCategories(
    formData.taxCategories,
    initialValues.taxCategories
  )

  const handleSubmit = async (_formValues: FormValues) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      const selectedState =
        _formValues.state === NATIONWIDE ? undefined : _formValues.state

      const formValues = {
        ..._formValues,
        state: selectedState
      }
      if (isEditing) {
        const formDataWithOnlyModifiedTaxCategories = {
          ...formValues,
          taxCategories: getModifiedTaxCategories(
            formValues.taxCategories,
            initialValues.taxCategories
          )
        }

        await onSubmit(
          formDataWithOnlyModifiedTaxCategories,
          taxRatesForCountry,
          selectedState as State
        )
      } else {
        const newTaxRatesAmountsForEachCategory = Object.entries(
          formValues.taxCategories || {}
        ).reduce(
          (acc, [taxCategoryId, amount]) => {
            // For adding new rates, include only non-empty values
            if (amount !== '' && amount !== undefined && amount !== null) {
              return {
                ...acc,
                [taxCategoryId]: decimalFromPercentage(amount)
              }
            }

            return acc
          },
          {} as FormValues['taxCategories']
        )

        const formDataWithNewTaxRateAmounts = {
          ...formValues,
          taxCategories: newTaxRatesAmountsForEachCategory
        }

        await onSubmit(
          formDataWithNewTaxRateAmounts,
          taxRatesForCountry,
          selectedState as State
        )
      }

      if (options.closeOnSubmit) {
        onClose()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Disable submit if no country is selected or no tax rates are filled
  const submitDisabled =
    !queries.isValid ||
    !Object.values(queries.formData.taxCategories).some(
      rate => rate !== '' && rate !== undefined && rate !== null
    )

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        {isEditing ? 'Edit tax rate' : 'Add tax rate'}
      </Modal.Header>

      <Modal.Content minHeight="auto" maxHeight="auto">
        <Flex flexDirection="column" gap="16px">
          <InputSelectControlField
            data-testid="addTaxRates.country"
            label="Country"
            onChange={value =>
              fieldsConfig.country.onChange(value as CountriesAlpha2)
            }
            options={Object.entries(countriesAlpha2).map(
              ([countryCode, countryName]) => ({
                label: `${
                  countriesAlpha2Emojis[countryCode as CountriesAlpha2]
                } ${countryName}`,
                value: countryCode,
                // If we're creating and there's already a tax rate configured for a country,
                // disable it in the dropdown
                disabled:
                  !isEditing &&
                  !SUPPORTED_COUNTRIES_WITH_STATES.includes(
                    countryCode as CountriesAlpha2
                  ) &&
                  Object.hasOwn(countriesWithTaxRates, countryCode)
              })
            )}
            validationErrors={fieldsConfig.country.validationErrors}
            initialValue={fieldsConfig.country.value || ''}
            placeholder="Select a country..."
            disabled={fieldsConfig.country.disabled}
          />
          {queries.formData.country &&
          SUPPORTED_COUNTRIES_WITH_STATES.includes(queries.formData.country) ? (
            <InputSelectControlField
              data-testid="addTaxRates.state"
              label="State"
              onChange={value => fieldsConfig.state.onChange(value as string)}
              options={
                preselectedState
                  ? [
                      NATIONWIDE_OPTION,
                      stateOptions.find(
                        option => option.value === preselectedState
                      )
                    ].filter(
                      (option): option is { label: string; value: string } =>
                        Boolean(option)
                    )
                  : stateOptions.map(option => {
                      return {
                        ...option,
                        disabled: alreadyDefinedStateTaxRates.includes(
                          option.value
                        )
                      }
                    })
              }
              validationErrors={fieldsConfig.state.validationErrors}
              initialValue={fieldsConfig.state.value || ''}
              placeholder="Select a state..."
              disabled={fieldsConfig.state.disabled}
            />
          ) : null}

          {taxCategories.length > 0 && (
            <>
              <Box>
                <Box {...Primary14SemiBold} mb="8px">
                  Tax rates
                </Box>
                <Flex flexDirection="column" gap={2}>
                  {taxCategories.map(category => (
                    <Flex
                      key={category.id}
                      backgroundColor={GreyGrey10}
                      height="40px"
                      paddingLeft={4}
                      paddingRight={1}
                      borderRadius={6}
                      py={2}
                      justifyContent="space-between"
                      align="center"
                    >
                      <Flex maxW="75%" gap={1} align="center">
                        <Text
                          data-testid={`addTaxRates.category.${category.name}`}
                          isTruncated
                        >
                          {category.name}
                        </Text>
                        {category.description ? (
                          <Tooltip placement="top" label={category.description}>
                            <IconCircleInfo color={GreyGrey60} />
                          </Tooltip>
                        ) : null}
                      </Flex>
                      <Box width="100px">
                        <PercentageInput
                          min={0}
                          max={100}
                          data-testid={`addTaxRates.category.${category.id}`}
                          variant="v2"
                          precision={5}
                          clampValueOnBlur={false}
                          onBlur={() => {
                            // This sets the value of the input to 0
                            // if a user deleted the value that was in it previously.
                            // Avoids submitting an empty string and explicitly shows the value was zeroed.
                            if (
                              initialValues.taxCategories[category.id] !== '' &&
                              fieldsConfig[`taxCategories.${category.id}`]
                                .value === ''
                            ) {
                              fieldsConfig[
                                `taxCategories.${category.id}`
                              ].onChange('0' as never)
                            }

                            // If less than zero, set to 0
                            if (
                              parseFloat(
                                fieldsConfig[`taxCategories.${category.id}`]
                                  .value
                              ) < 0
                            ) {
                              fieldsConfig[
                                `taxCategories.${category.id}`
                              ].onChange('0' as never)
                            }

                            // If greater than 100, set to 100
                            if (
                              parseFloat(
                                fieldsConfig[`taxCategories.${category.id}`]
                                  .value
                              ) > 100
                            ) {
                              fieldsConfig[
                                `taxCategories.${category.id}`
                              ].onChange('100' as never)
                            }
                          }}
                          placeholder="0"
                          value={
                            fieldsConfig[`taxCategories.${category.id}`].value
                          }
                          onChange={value => {
                            fieldsConfig[
                              `taxCategories.${category.id}`
                            ].onChange(value as never)
                          }}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            </>
          )}
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Button
          data-testid={dataTestId ? `${dataTestId}.cancel` : undefined}
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Modal.SubmitButton
          data-testid={dataTestId}
          disabled={
            isSubmitting ||
            (isEditing ? isEmpty(modifiedTaxCategories) : submitDisabled)
          }
          onClick={() => {
            void handleSubmit(formData)
          }}
          submitting={isSubmitting}
          text={submitText}
          submittingText={submittingText}
        />
      </Modal.Footer>
    </>
  )
}

/**
 * Controlled variant for flows that mount the modal themselves (e.g. the
 * stepped confirmation modals) instead of opening it from a trigger.
 */
export const ControlledTaxRatesModal = ({
  onClose,
  'data-testid': dataTestId,
  ...props
}: Omit<Props, 'trigger'> & { onClose: () => void }) => {
  return (
    <Modal isOpen onClose={onClose} data-testid={dataTestId}>
      <TaxRatesModalUI {...props} onClose={onClose} data-testid={dataTestId} />
    </Modal>
  )
}

export const TaxRatesModal = ({
  trigger,
  'data-testid': dataTestId,
  ...props
}: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  const triggerElement: ReactElement | undefined =
    typeof trigger === 'function' ? trigger(openModal) : trigger

  const triggerWithOnClick =
    triggerElement &&
    cloneElement(triggerElement, {
      onClick: (event: React.MouseEvent) => {
        if (triggerElement.props.onClick) {
          triggerElement.props.onClick(event)
        }
        openModal()
      }
    })

  return (
    <>
      {triggerWithOnClick}

      <Modal isOpen={isModalOpen} onClose={closeModal} data-testid={dataTestId}>
        <TaxRatesModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}
